import api from './api';

class MaintenanceService {
  constructor() {
    this.endpoint = '/maintenances';
  }

  /**
   * Obtener todos los registros de mantenimiento
   */
  async getAll(filters = {}) {
    try {
      const response = await api.get(this.endpoint, { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error obteniendo mantenimientos:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Obtener un registro de mantenimiento por ID
   */
  async getById(id) {
    try {
      const response = await api.get(`${this.endpoint}/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error obteniendo mantenimiento por ID:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Crear un nuevo registro de mantenimiento
   */
  async create(maintenanceData) {
    try {
      // Verificar si serie está presente
      if (!maintenanceData.serie) {
        console.error('Error: Serie es nulo o vacío', maintenanceData);
        throw new Error('El número de serie del vehículo es obligatorio');
      }
      
      console.log('Datos de mantenimiento a enviar:', maintenanceData);
      
      // Convertir del formato de UI al formato de API
      const apiData = {
        // Use keys matching backend expectations (lowercase)
        truckId: maintenanceData.truckId,
        serie: maintenanceData.serie,
        fechaMantenimiento: maintenanceData.fechaMantenimiento,
        kilometrajeActual: maintenanceData.kilometrajeActual,
        tipoMantenimiento: maintenanceData.tipoMantenimiento,
        nombreOperador: maintenanceData.nombreOperador || '',
        nombreJefeTaller: maintenanceData.nombreJefeTaller || '',
        nombreMecanico: maintenanceData.nombreMecanico || '',
        nombreSupervisor: maintenanceData.nombreSupervisor || '',
        requisicionMaterial1: maintenanceData.requisicionMaterial1 || '',
        requisicionMaterial2: maintenanceData.requisicionMaterial2 || '',
        requisicionMaterial3: maintenanceData.requisicionMaterial3 || '',
        requisicionMaterial4: maintenanceData.requisicionMaterial4 || '',
        requisicionMaterial5: maintenanceData.requisicionMaterial5 || '',
        requisicionMaterial6: maintenanceData.requisicionMaterial6 || '',
        requisicionMaterial7: maintenanceData.requisicionMaterial7 || '',
        observaciones: maintenanceData.observaciones || '',
        // Compute next maintenance date if needed (optional)
        proximoMantenimiento: maintenanceData.fechaMantenimiento ? new Date(new Date(maintenanceData.fechaMantenimiento).setMonth(new Date(maintenanceData.fechaMantenimiento).getMonth() + 3)).toISOString().split('T')[0] : null,
        numero_economico: maintenanceData.numeroEconomico || maintenanceData.numero_economico || ''
      };
      
      console.log('API data a enviar:', apiData);

      const response = await api.post(this.endpoint, apiData);
      return response.data;
    } catch (error) {
      console.error('Error creando mantenimiento:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Actualizar un registro de mantenimiento
   */
  async update(id, maintenanceData) {
    try {
      const response = await api.put(`${this.endpoint}/${id}`, maintenanceData);
      return response.data.data;
    } catch (error) {
      console.error('Error actualizando mantenimiento:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Eliminar un registro de mantenimiento
   */
  async delete(id) {
    try {
      const response = await api.delete(`${this.endpoint}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error eliminando mantenimiento:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Generar XLSX de mantenimiento
   */
  async generatePDF(id) {
    try {
      // TEMPORARY: Use existing test route that doesn't require authentication
      const response = await api.get(`${this.endpoint}/test/${id}/pdf`, { responseType: 'blob' });
      // Validate content-type before saving: if backend returned JSON/HTML (error page) we should not save it as .xlsx
      const contentType = (response.headers && (response.headers['content-type'] || response.headers['Content-Type'])) || '';
      if (!/openxmlformats-officedocument|vnd\.ms-excel|zip|octet-stream/i.test(contentType)) {
        // Try to extract error message from the returned blob
        let bodyText = '';
        try { bodyText = await response.data.text(); } catch (e) { /* ignore */ }
        const errMsg = bodyText ? `Respuesta inesperada del servidor: ${bodyText}` : `Tipo de contenido inesperado: ${contentType}`;
        console.error('generatePDF: invalid content-type', contentType, 'body:', bodyText);
        throw new Error(errMsg);
      }
      
      // Try to obtain filename from Content-Disposition header
      let filename = `mantenimiento_${id}.xlsx`; // Fallback genérico
      
      console.log('📋 Response headers:', response.headers);
      console.log('📋 All header keys:', Object.keys(response.headers || {}));
      
      const cd = response.headers && (response.headers['content-disposition'] || response.headers['Content-Disposition']);
      console.log('📋 Content-Disposition encontrado:', cd);
      
      if (cd) {
        console.log('✅ Content-Disposition header:', cd);
        // Mejorado: capturar filename con espacios correctamente
        const match = cd.match(/filename\*=UTF-8''([^;\n\r]+)|filename="([^"]+)"|filename=([^;\s]+)/i);
        if (match) {
          filename = decodeURIComponent(match[1] || match[2] || match[3]);
          console.log('✅ Filename extraído:', filename);
        } else {
          console.warn('⚠️ No se pudo extraer filename del header');
        }
      } else {
        console.warn('⚠️ No se encontró header Content-Disposition, usando fallback:', filename);
      }
      
  const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return { success: true, message: 'Excel generado y descargado correctamente' };
    } catch (error) {
      console.error('Error generando PDF:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Compatibilidad hacia atrás: algunos builds antiguos invocan generateExcel().
   * Redirigimos al método actual para evitar TypeError en producción.
   */
  async generateExcel(id) {
    return this.downloadExcelComplete(id);
  }

  /**
   * Subir PDF a un mantenimiento
   */
  async uploadPDF(id, file) {
    try {
      const formData = new FormData();
      formData.append('pdf', file);
      
      const response = await api.post(`${this.endpoint}/${id}/upload-pdf`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error subiendo PDF:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Ver/Descargar PDF de un mantenimiento
   */
  async viewPDF(id) {
    try {
      const response = await api.get(`${this.endpoint}/${id}/pdf-documento`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Abrir en nueva pestaña
      window.open(url, '_blank');
      
      // Limpiar después de un tiempo
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      
      return { success: true };
    } catch (error) {
      console.error('Error viendo PDF:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Eliminar PDF de un mantenimiento
   */
  async deletePDF(id) {
    try {
      const response = await api.delete(`${this.endpoint}/${id}/pdf-documento`);
      return response.data;
    } catch (error) {
      console.error('Error eliminando PDF:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Descargar Excel completo con datos del mantenimiento
   */
  async downloadExcelComplete(id) {
    try {
      const response = await api.get(`${this.endpoint}/download/${id}/xlsx`, {
        responseType: 'blob'
      });
      
      // Crear un objeto URL a partir del blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Obtener el nombre del archivo desde el header Content-Disposition
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Mantenimiento_${id}.xlsx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar después de un tiempo
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      
      return { success: true };
    } catch (error) {
      console.error('Error descargando Excel completo:', error);
      throw this.handleError(error);
    }
  }

  // Manejar errores de la API
  handleError(error) {
    if (error.response) {
      // Error de respuesta del servidor
      const message = error.response.data?.message || 'Error del servidor';
      return new Error(message);
    } else if (error.request) {
      // Error de red
      return new Error('Error de conexión con el servidor');
    } else {
      // Error general
      return new Error(error.message || 'Error desconocido');
    }
  }
  
  // Obtener opciones para el tipo de mantenimiento
  getTipoMantenimientoOptions() {
    return [
      { value: 'Preventivo', label: 'Mantenimiento Preventivo' },
      { value: 'Correctivo', label: 'Mantenimiento Correctivo' }
    ];
  }
}

export const maintenanceService = new MaintenanceService();